<template>
  <div class="fixed inset-0 z-30 flex justify-end bg-gray-800/60" role="dialog" aria-modal="true" aria-labelledby="hermes-start-title" @click.self="$emit('close')">
    <div class="w-[560px] h-full bg-white border-l border-gray-200 flex flex-col">
      <div class="bg-primary-100 px-6 py-4 flex items-center">
        <h2 id="hermes-start-title" class="text-[14px] font-semibold text-gray-900">Start a run</h2>
        <button type="button" class="ml-auto text-gray-700 hover:text-gray-900" @click="$emit('close')">Close</button>
      </div>
      <div class="flex-1 overflow-y-auto p-6 space-y-6">
        <section>
          <h3 class="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-2">Executor</h3>
          <label
v-for="e in activeExecutors" :key="e.id"
                 :class="['flex gap-3 rounded-md border p-3 mb-2 cursor-pointer', executor === e.key ? 'border-primary-500 bg-primary-50' : 'border-gray-200']">
            <input v-model="executor" type="radio" :value="e.key" class="mt-1">
            <span>
              <span class="block font-medium text-gray-900">{{ e.name }}</span>
              <span class="block text-gray-600">{{ words(e.runtime) }} · {{ e.model }}{{ e.reasoning ? ` · ${e.reasoning}` : '' }}</span>
              <span class="block text-gray-500">{{ e.identity === 'self' ? 'Acts as itself' : 'Acts as you' }}</span>
            </span>
          </label>
        </section>

        <section>
          <h3 class="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-2">Template</h3>
          <select v-model="templateKey" :class="INPUT">
            <option value="">Choose a template</option>
            <option v-for="t in activeTemplates" :key="t.id" :value="String(t.id)">{{ t.name }}</option>
          </select>
          <p v-if="template" class="text-gray-500 mt-2">
            v{{ template.version }} · unit {{ template.unit || 'none' }} · {{ template.population ? `population DataAttribute #${template.population.ref}` : 'no population (one unit per run)' }}
            · {{ template.inputs.length }} inputs
          </p>
        </section>

        <section v-if="template && !template.population">
          <label class="block">
            <span class="block text-gray-500 mb-2">{{ template.unit || 'Unit' }} <span class="text-gray-900">· Required</span></span>
            <input v-model="unitValue" :class="[INPUT,' sensitive', missing.includes('unit') ? 'bg-red-tint' : '']">
          </label>
        </section>

        <section v-if="template">
          <h3 class="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-2">Run parameters</h3>
          <p v-if="!template.params.length" class="rounded-md bg-neutral-50 px-3 py-2 text-gray-900">This template declares no run parameters.</p>
          <label v-for="p in template.params" :key="p.name" class="block mb-3">
            <span class="block text-gray-500 mb-2">
              <span class="text-gray-900">{{ p.name }}</span> · {{ p.required ? 'Required' : 'Optional; leaving it empty removes the clause' }} · used in {{ p.where.join(', ') }}
            </span>
            <input v-model="params[p.name]" :class="[INPUT, missing.includes(p.name) ? 'bg-red-tint' : '']">
          </label>
          <label v-if="template.population" class="block">
            <span class="block text-gray-500 mb-2"><span class="text-gray-900">sample</span> · Optional; score a fixed draw of this many units instead of the whole population</span>
            <input v-model.number="sample" type="number" min="1" :class="[SIZED_INPUT, 'w-40']">
          </label>
        </section>

        <section v-if="template && selectedExecutor">
          <h3 class="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-2">Writes</h3>
          <p v-if="!template.effects.length" class="text-gray-600">None. This template produces results and frames only.</p>
          <ul v-else class="space-y-1">
            <li v-for="k in template.effects" :key="k">{{ k }}: {{ grantWord(k) }}</li>
          </ul>
        </section>

        <p v-if="estimate" class="text-gray-600">{{ estimate }}</p>
      </div>
      <div class="border-t border-gray-200 px-6 py-4 space-y-3">
        <div v-if="error" role="alert" class="rounded-md bg-red-tint px-3 py-2 text-gray-900">{{ error }}</div>
        <div class="flex justify-end gap-3">
          <button type="button" :class="SECONDARY_BUTTON" @click="$emit('close')">Cancel</button>
          <button type="button" :class="PRIMARY_BUTTON" :disabled="!executor || !template || busy" @click="start">{{ busy ? 'Starting…' : 'Start run' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API, INPUT, SIZED_INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, errorOf, money, words } from './hermes';

export default {
  name: 'HermesStartPanel',
  props: {
    executors: { type: Array, required: true },
    templates: { type: Array, required: true },
    templateId: { type: Number, default: null },
  },
  emits: ['close'],
  data() {
    return {
      executor: '', templateKey: this.templateId ? String(this.templateId) : '', params: {}, sample: null, unitValue: '',
      busy: false, error: '', missing: [], lastRun: null, INPUT, SIZED_INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON,
    };
  },
  computed: {
    activeExecutors() {
      return this.executors.filter((e) => e.usable);
    },
    activeTemplates() {
      return this.templates.filter((t) => t.is_active);
    },
    template() {
      return this.templates.find((t) => String(t.id) === this.templateKey) || null;
    },
    selectedExecutor() {
      return this.executors.find((e) => e.key === this.executor) || null;
    },
    estimate() {
      const run = this.lastRun;
      if (!run || !(run.progress || {}).done || !(run.usage || {}).cost_usd) return '';
      const perUnit = run.usage.cost_usd / run.progress.done;
      if (!this.template.population) return `About ${money(perUnit)} per run, from run #${run.id}.`;
      if (this.sample) return `About ${money(perUnit * this.sample)} for ${this.sample} units, at ${money(perUnit)} per unit from run #${run.id}.`;
      return `${money(perUnit)} per unit on run #${run.id}; the full population costs that times its size.`;
    },
  },
  watch: {
    templateKey() {
      this.params = {};
      this.missing = [];
      this.loadLastRun();
    },
    executor() {
      this.loadLastRun();
    },
  },
  mounted() {
    const scorer = this.activeExecutors.find((e) => e.runtime === 'single_call') || this.activeExecutors[0];
    this.executor = scorer ? scorer.key : '';
  },
  methods: {
    words,
    grantWord(kind) {
      const e = this.selectedExecutor;
      if (e.identity === 'requester') return 'proposed, using your own permissions';
      const g = e.grants.find((x) => x.kind === kind);
      return g ? (g.mode === 'direct' ? 'written directly' : 'proposed for approval') : 'not allowed; the run will be refused';
    },
    async loadLastRun() {
      this.lastRun = null;
      if (!this.template || !this.selectedExecutor) return;
      try {
        const { data } = await this.$axios.get(`${API}/runs/`, { params: { template: this.template.id, executor: this.selectedExecutor.id, status: 'completed', limit: 1 } });
        this.lastRun = data.results[0] || null;
      } catch {
        this.lastRun = null;
      }
    },
    async start() {
      this.error = '';
      this.missing = this.template.params.filter((p) => p.required && !String(this.params[p.name] ?? '').trim()).map((p) => p.name);
      if (!this.template.population && !this.unitValue.trim()) this.missing.push('unit');
      if (this.missing.length) {
        this.error = `Fill in ${this.missing.map((m) => (m === 'unit' ? this.template.unit : m)).join(', ')} before starting.`;
        return;
      }
      const params = Object.fromEntries(Object.entries(this.params).filter(([, v]) => String(v ?? '').trim() !== ''));
      if (this.sample) params.sample = this.sample;
      const body = { executor: this.executor, template: this.template.id, params };
      if (!this.template.population) body.unit = { [this.template.unit]: this.unitValue.trim() };
      this.busy = true;
      try {
        const { data } = await this.$axios.post(`${API}/runs/start/`, body);
        this.$router.push({ name: 'AdminHermesRun', params: { runId: data.id } });
      } catch (e) {
        this.error = errorOf(e, 'The run was not started.');
        const named = (this.template.params || []).map((p) => p.name).filter((n) => this.error.includes(n));
        this.missing = named;
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>
